import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    Case,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    QueryResult,
    CaseFormsResponse,
    QueryParams,
    AdminDashboard,
    defaultAdminDashboard,
    MutationResult,
    ProductCodeResponse,
} from "../models";
import { Lists } from "../components";
import { useNavigate } from "react-router-dom";

export interface IUseCaseQuery
    extends Omit<IUseQueryPaged<Case>, "createMutation" | "updateMutation" | "deleteMutation"> {
    formSummaryQuery: QueryResult<CaseFormsResponse>;
    adminDashboardQuery: QueryResult<AdminDashboard>;
    productCodeQuery: QueryResult<ProductCodeResponse[]>;
    createMutation: MutationResult<Case, unknown, void>;
}

export interface UseCaseQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "forms" | "adminDashboard" | "sideList" | "productCodes";
    includeDetails?: boolean;
    referralStatus?: string;
    listSlug?: string;
    tissueId?: number | string;
    searchParams?: Record<string, string>;
}

export const useCaseQuery = (params?: UseCaseQueryParams): IUseCaseQuery => {
    const caseId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const navigate = useNavigate();
    const { selectedTerritoryIds } = useTerritories();
    const { isAuthorized } = useAuth();
    const { get, post } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Cases);
    const url = Endpoints.Cases;
    const includeDetailsParam = params?.includeDetails ? "true" : "false";
    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        territoryIds: selectedTerritoryIds.join(","),
        referralStatus: params?.referralStatus ?? queryParams.referralStatus,
        includeDetails: includeDetailsParam,
        listSlug: params?.listSlug ?? queryParams.listSlug,
    };

    const queryType = useMemo(() => {
        if (params?.queryType === "forms") return "forms";
        if (params?.queryType === "adminDashboard") return "adminDashboard";
        if (params?.queryType === "sideList" || !!params?.listSlug) return "sideList";
        if (params?.queryType === "productCodes") return "productCodes";
        return getQueryType(caseId, params?.queryType);
    }, [params?.queryType, params?.listSlug, caseId]);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Case>>(
                getRequestOptions(
                    url,
                    { ...config, params: { ...config?.params, ...params?.searchParams } },
                    queryParamsWithArgs,
                    sortParams,
                    pageParams
                )
            ),
        queryKey: queryKeys.cases.pagedList({
            ...queryParamsWithArgs,
            ...pageParams,
            ...sortParams,
            ...params?.searchParams,
        }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Case>>(
                getRequestOptions(url, config, queryParamsWithArgs, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.cases.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: false,
        cacheTime: 2000,
        staleTime: 2000,
    });

    const formSummaryQuery = useGenericQuery({
        queryFn: (config) => get<CaseFormsResponse>({ url: `${url}/${caseId}/${params?.tissueId ?? 0}/forms`, config }),
        queryKey: queryKeys.cases.forms(caseId),
        placeholderData: { formDataSummaries: [] },
        enabled: isAuthorized && queryType === "forms" && !!caseId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Case>({ url: `${url}/${caseId}?includeDetails=${includeDetailsParam}`, config }),
        queryKey: params?.includeDetails ? queryKeys.cases.detailDeep(caseId) : queryKeys.cases.detail(caseId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!caseId,
        staleTime: 5000,
        cacheTime: 5000,
    });

    const productCodeQuery = useGenericQuery({
        queryFn: (config) => get<ProductCodeResponse[]>({ url: `${url}/${caseId}/product-codes`, config }),
        queryKey: queryKeys.cases.productCode(caseId),
        placeholderData: [],
        enabled: isAuthorized && queryType === "productCodes" && !!caseId,
    });

    const adminDashboardQuery = useGenericQuery({
        queryFn: (config) => get<AdminDashboard>({ url: `${url}/dashboard/admin`, config }),
        queryKey: queryKeys.dashboards.admin(),
        placeholderData: defaultAdminDashboard,
        enabled: isAuthorized && queryType === "adminDashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const createMutation = useGenericMutation({
        mutationFn: () => post<Partial<Case>, Case>({ url }),
        queryKey: queryKeys.lists.all(),
        disableSuccessToast: true,
        onSuccess: (_case: Case) => {
            navigate(`/lists/${Lists.ReferralsNew}/cases/${_case.id}/referral/${_case.referral?.id}`);
        },
    });

    return useMemo<IUseCaseQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            formSummaryQuery,
            detailQuery,
            productCodeQuery,
            adminDashboardQuery,
            createMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            formSummaryQuery,
            detailQuery,
            productCodeQuery,
            adminDashboardQuery,
            createMutation,
        ]
    );
};
